/**
 * Builds a nested tree from the flat project list returned by GET /api/projects.
 * Orphans (unknown parent_id, broken FK in pathological cases) are surfaced as
 * top-level so the user can see and resolve them rather than silently lose
 * them.
 */

import type { Project, ProjectNode } from '@/types/models';

export function buildTree(flat: Project[]): ProjectNode[] {
  const byId = new Map<string, ProjectNode>();
  for (const p of flat) byId.set(p.id, { ...p, children: [] });

  const roots: ProjectNode[] = [];
  for (const node of byId.values()) {
    if (node.parentId && byId.has(node.parentId)) {
      byId.get(node.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  // Stable sort children by name (case-insensitive).
  const sortByName = (a: ProjectNode, b: ProjectNode): number =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });

  function sortRecursive(nodes: ProjectNode[]): void {
    nodes.sort(sortByName);
    for (const n of nodes) sortRecursive(n.children);
  }
  sortRecursive(roots);

  return roots;
}
